import { MenuOutlined, RightOutlined, SearchOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import { useTranslate } from "../i18n";
import { findNavGroup, findNavItem } from "../navigation";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { UserMenu } from "./UserMenu";

interface TopbarProps {
    onOpenMenu: () => void;
    onOpenCommandPalette: () => void;
}

/** True on macOS, where the palette shortcut is ⌘K rather than Ctrl K. */
const isApple = () => /Mac|iPhone|iPad/.test(navigator.platform ?? "");

/**
 * The persistent bar: where am I, how do I get somewhere else, who am I.
 *
 * The app had no header at all — the only chrome was a sidebar that vanished on
 * a narrow screen. The trail names the group as well as the section, which is
 * what makes the grouped sidebar legible.
 */
export const Topbar = ({ onOpenMenu, onOpenCommandPalette }: TopbarProps) => {
    const location = useLocation();
    const t = useTranslate();

    const item = findNavItem(location.pathname);
    const group = findNavGroup(item);

    return (
        <header className="topbar">
            <button
                type="button"
                className="topbar__icon-button"
                onClick={onOpenMenu}
                aria-label={t("common.menu")}
            >
                <MenuOutlined />
            </button>

            <div className="topbar__trail">
                {group != null ? (
                    <>
                        <span>{t(group.labelKey)}</span>
                        <RightOutlined className="topbar__trail-sep" />
                    </>
                ) : null}
                <span className="topbar__trail-current">
                    {item != null ? t(item.labelKey) : t("account.title")}
                </span>
            </div>

            <div className="topbar__spacer" />

            <button
                type="button"
                className="cmd-trigger"
                onClick={onOpenCommandPalette}
                aria-label={t("common.search")}
            >
                <SearchOutlined />
                <span className="cmd-trigger__label">{t("common.search")}</span>
                <span className="kbd">{isApple() ? "⌘K" : "Ctrl K"}</span>
            </button>

            <LanguageSwitcher />
            <UserMenu />
        </header>
    );
};
